/**
 * Deep Link Service
 * Handles Universal Links (iOS), App Links (Android), and custom schemes
 * 
 * @author Varun Pandey
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { closeInAppBrowserIfOpen } from '@/instagram/platformAdapter';
import { INSTAGRAM_BRIDGE_DEEP_LINK_FLAG } from '@/pages/auth/InstagramBridgePage';

export type DeepLinkType = 'instagram-callback' | 'google-callback' | 'booking' | 'profile' | 'unknown';

export interface DeepLinkData {
  type: DeepLinkType;
  params: Record<string, string>;
}

export class DeepLinkService {
  private static listeners: Array<(data: DeepLinkData) => void> = [];
  /** One-shot guard: process Instagram bridge URL only once per app session to avoid appUrlOpen loop. */
  private static instagramBridgeHandled = false;

  /**
   * Reset the Instagram bridge one-shot guard so a subsequent OAuth (e.g. connect from gallery/social) can be processed.
   * Called by Bridge after successfully handling connect/photos context.
   */
  static resetInstagramBridgeHandled(): void {
    this.instagramBridgeHandled = false;
  }

  /**
   * Initialize deep link handling
   */
  static async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // Web: Handle URL changes
      window.addEventListener('popstate', () => {
        this.handleUrl(window.location.href);
      });
      
      // Check initial URL
      this.handleUrl(window.location.href);
      return;
    }

    // Native: Listen to app URL events
    App.addListener('appUrlOpen', (event: { url: string }) => {
      this.handleUrl(event.url);
    });

    // Check if app was opened with a URL
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      this.handleUrl(launchUrl.url);
    }
  }

  /**
   * Handle incoming URL
   */
  private static handleUrl(url: string): void {
    try {
      const parsed = this.parseUrl(url);
      if (parsed) {
        this.notifyListeners(parsed);
      }
    } catch (error) {
      console.error('Deep link handling error:', error);
    }
  }

  /**
   * Parse URL into DeepLinkData
   */
  private static parseUrl(url: string): DeepLinkData | null {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;

      // Instagram callback – Bridge component handles SSO; we only navigate on native.
      if (path.includes('/instagram-bridge') || path.includes('/instagram/callback')) {
        const code = urlObj.searchParams.get('code');
        const state = urlObj.searchParams.get('state');
        const error = urlObj.searchParams.get('error');

        if (error) {
          throw new Error(`[DeepLink] Instagram OAuth error: ${error}`);
        }

        if (code && state) {
          if (Capacitor.isNativePlatform()) {
            if (DeepLinkService.instagramBridgeHandled) {
              return { type: 'instagram-callback', params: { code, state } };
            }
            DeepLinkService.instagramBridgeHandled = true;
            console.log('[DeepLink] Instagram-bridge Universal Link / App Link detected');
            const pathAndSearch =
              (path.includes('/instagram-bridge') ? '/instagram-bridge' : '/instagram/callback') +
              urlObj.search +
              (urlObj.hash || '');
            const bridgeUrl = window.location.origin + pathAndSearch;
            closeInAppBrowserIfOpen()
              .catch((err) => console.warn('[DeepLink] Browser.close failed:', err))
              .then(() => {
                sessionStorage.setItem(INSTAGRAM_BRIDGE_DEEP_LINK_FLAG, '1');
                window.location.replace(bridgeUrl);
              });
          }
          return {
            type: 'instagram-callback',
            params: { code, state },
          };
        }
      }

      // Google callback (implicit flow returns id_token in hash)
      if (path.includes('/google-bridge') || path.includes('/google/callback')) {
        // Native google auth is now synchronous and handled directly, this deep link 
        // listener is technically no longer needed for native apps, but left for backward 
        // compatibility or web specific edge cases if applicable.
        const error = urlObj.searchParams.get('error');
        if (error) {
          throw new Error(`Google OAuth error: ${error}`);
        }
      }

      // Booking link
      if (path.includes('/booking/')) {
        const bookingId = path.split('/booking/')[1];
        return {
          type: 'booking',
          params: { bookingId },
        };
      }

      // Profile link
      if (path.includes('/profile/')) {
        const userId = path.split('/profile/')[1];
        return {
          type: 'profile',
          params: { userId },
        };
      }

      return {
        type: 'unknown',
        params: {},
      };
    } catch (error) {
      console.error('URL parsing error:', error);
      return null;
    }
  }

  /**
   * Add deep link listener
   */
  static addListener(callback: (data: DeepLinkData) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  private static notifyListeners(data: DeepLinkData): void {
    this.listeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error('Deep link listener error:', error);
      }
    });
  }
}
